import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { RecordDialog } from '../components/RecordDialog';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const emptyForm = () => ({ name: '', phone_number: '', ip_address: '', ip_check_enabled: true });

const pickPartner = (data) => data?.partner ?? data;

const rowToForm = (row) => ({
  name: row?.name || '',
  phone_number: row?.phone_number || row?.phone || '',
  ip_address: row?.ip_address || '',
  ip_check_enabled: row?.ip_check_enabled !== false,
});

const toPayload = (form) => ({
  name: form.name.trim(),
  phone_number: form.phone_number.trim(),
  ip_address: form.ip_address.trim(),
  ip_check_enabled: form.ip_check_enabled,
});

const formIsValid = (form) =>
  Boolean(form.name.trim()) && (!form.ip_check_enabled || Boolean(form.ip_address.trim()));

export function PartnersPage() {
  const { t } = useAppUi();
  const { admin } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);

  const canWrite = admin?.role === 'SUPER_ADMIN' || admin?.role === 'ADMIN';

  const [createOpen, setCreateOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [viewOpen, setViewOpen] = React.useState(false);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [accounts, setAccounts] = React.useState([]);
  const [linkAccount, setLinkAccount] = React.useState(null);
  const [createdOpen, setCreatedOpen] = React.useState(false);

  const [selected, setSelected] = React.useState(null);
  const [createdPartner, setCreatedPartner] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/partners', {
        params: { take: pageSize, page: page + 1 },
      });
      const data = unwrapApiResponse(res);
      setRows(data?.partners || data?.items || []);
      setRowCount(data?.total || 0);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setForm(emptyForm());
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setForm(rowToForm(row));
    setEditOpen(true);
  };

  const openView = (row) => {
    setSelected(row);
    setViewOpen(true);
  };

  const openLink = async (row) => {
    setSelected(row);
    setLinkAccount(row.user_id ? { id: row.user_id, agency_name: row.account_name } : null);
    setLinkOpen(true);
    try {
      const res = await http.get('/admin/subscriptions');
      setAccounts(unwrapApiResponse(res) || []);
    } catch (e) {
      setError(e);
    }
  };

  const saveLink = async () => {
    if (!selected?.id) return;
    setSaving(true);
    setError(null);
    try {
      await http.put(`/admin/partners/${selected.id}/account`, { user_id: linkAccount?.id ?? null });
      setLinkOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setSelected(row);
    setDeleteOpen(true);
  };

  const createPartner = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await http.post('/admin/partners', toPayload(form));
      const data = unwrapApiResponse(res);
      const partner = pickPartner(data);
      setCreateOpen(false);
      setForm(emptyForm());
      setCreatedPartner(partner);
      setCreatedOpen(true);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const updatePartner = async () => {
    if (!selected?.id) return;
    setSaving(true);
    setError(null);
    try {
      await http.put(`/admin/partners/${selected.id}`, toPayload(form));
      setEditOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const deletePartner = async () => {
    if (!selected?.id) return;
    setSaving(true);
    setError(null);
    try {
      await http.delete(`/admin/partners/${selected.id}`);
      setDeleteOpen(false);
      await fetchData();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  function PartnerActionsCell({ row }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const close = () => setAnchorEl(null);

    const onAction = async (fn) => {
      close();
      await fn();
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
          aria-label={t('partners.colActions')}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={(e) => {
            e?.stopPropagation?.();
            close();
          }}
          onClick={(e) => e.stopPropagation()}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.25,
                minWidth: 200,
              },
            },
          }}
        >
          <MenuItem onClick={() => onAction(() => openView(row))}>{t('common.view')}</MenuItem>
          <MenuItem disabled={!canWrite} onClick={() => onAction(() => openEdit(row))}>
            {t('common.edit')}
          </MenuItem>
          <MenuItem disabled={!canWrite} onClick={() => onAction(() => openLink(row))}>
            {t('partners.linkAccount')}
          </MenuItem>
          <MenuItem
            disabled={!canWrite}
            onClick={() => onAction(() => openDelete(row))}
            sx={{ color: 'error.main' }}
          >
            {t('common.delete')}
          </MenuItem>
        </Menu>
      </>
    );
  }

  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: t('partners.colId'), width: 90 },
      { field: 'name', headerName: t('partners.colName'), flex: 1, minWidth: 220 },
      {
        field: 'phone_number',
        headerName: t('partners.colPhone'),
        width: 160,
        valueGetter: (_, row) => row?.phone_number || row?.phone || '',
      },
      {
        field: 'ip_address',
        headerName: t('partners.colIpAddress'),
        width: 170,
        valueGetter: (_, row) =>
          row?.ip_check_enabled === false ? t('partners.ipCheckOff') : row?.ip_address || '',
      },
      {
        field: 'account_name',
        headerName: t('partners.colAccount'),
        width: 200,
        valueGetter: (_, row) =>
          row?.user_id ? `#${row.user_id} ${row.account_name || ''}` : t('partners.noAccount'),
      },
      { field: 'created_at', headerName: t('partners.colCreated'), width: 180 },
      {
        field: 'actions',
        headerName: t('partners.colActions'),
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (p) => <PartnerActionsCell row={p.row} />,
      },
    ],
    [t, canWrite]
  );

  const formFields = (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <TextField
        label={t('partners.colName')}
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        required
        autoComplete="off"
      />
      <TextField
        label={t('partners.colPhone')}
        value={form.phone_number}
        onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
        autoComplete="off"
      />
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={form.ip_check_enabled}
              onChange={(e) => setForm((f) => ({ ...f, ip_check_enabled: e.target.checked }))}
            />
          }
          label={t('partners.ipCheck')}
        />
        <FormHelperText sx={{ mt: 0 }}>{t('partners.ipCheckHint')}</FormHelperText>
      </Box>
      <TextField
        label={t('partners.colIpAddress')}
        value={form.ip_address}
        onChange={(e) => setForm((f) => ({ ...f, ip_address: e.target.value }))}
        placeholder="192.168.1.1"
        autoComplete="off"
        required={form.ip_check_enabled}
        disabled={!form.ip_check_enabled}
      />
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title={t('partners.title')}
        subtitle={t('partners.subtitle')}
        right={
          <Stack direction="row" spacing={1}>
            {canWrite ? (
              <Button variant="contained" onClick={openCreate}>
                {t('partners.create')}
              </Button>
            ) : null}
            <Button variant="outlined" onClick={fetchData} disabled={loading}>
              {t('common.refresh')}
            </Button>
          </Stack>
        }
      />

      <ErrorBanner error={error} />

      <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          density="compact"
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => {
            setPage(m.page);
            setPageSize(m.pageSize);
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('partners.dialogCreateTitle')}</DialogTitle>
        <DialogContent dividers>{formFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={createPartner}
            disabled={saving || !formIsValid(form)}
          >
            {saving ? t('common.creating') : t('partners.createBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('partners.dialogEditTitle', { id: selected?.id || '' })}</DialogTitle>
        <DialogContent dividers>{formFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={updatePartner}
            disabled={saving || !formIsValid(form)}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('partners.deleteTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {t('partners.deleteConfirm', { id: selected?.id ?? '', name: selected?.name ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={deletePartner} disabled={saving}>
            {saving ? t('common.saving') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('partners.linkTitle', { name: selected?.name || '' })}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Autocomplete
              options={accounts}
              value={linkAccount}
              onChange={(_, v) => setLinkAccount(v)}
              isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
              getOptionLabel={(o) => `#${o.id} ${o.agency_name || o.name || ''}${o.email ? ` · ${o.email}` : ''}`}
              renderInput={(params) => <TextField {...params} label={t('partners.colAccount')} />}
            />
            <FormHelperText>{t('partners.linkHint')}</FormHelperText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={saveLink} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <RecordDialog
        open={viewOpen}
        title={t('partners.dialogViewTitle', { id: selected?.id || '' })}
        record={selected}
        onClose={() => setViewOpen(false)}
      />

      <RecordDialog
        open={createdOpen}
        title={t('partners.dialogCreatedTitle')}
        record={createdPartner}
        onClose={() => setCreatedOpen(false)}
      />
    </Box>
  );
}
